import React, { useState } from 'react';
import { MessageSquareText, Send, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

/**
 * Grounded LLM Q&A over the latest Layer 1 risk report (POST /api/llm/assist).
 * Hidden unless llm_assist_enabled from /api/env.
 */
export function ReportAssistPanel({ hasReport, compact = false }) {
  const { context } = usePilotContext();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [last, setLast] = useState(null);

  if (!context.llm_assist_enabled) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (q.length < 3) {
      toast.error('Enter a question (at least a few words).');
      return;
    }
    setLoading(true);
    setLast(null);
    try {
      const data = await api.post(
        '/api/llm/assist',
        {
          question: q,
          context_mode: 'latest_risk_report',
        },
        { timeoutMs: 420000 }
      );
      setLast(data);
    } catch (err) {
      toast.error(err.message || 'Assist request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-secondary/40 to-background">
      <CardHeader className={cn(compact && 'pb-2')}>
        <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
          <MessageSquareText className="h-5 w-5 text-primary" />
          Ask about this report
        </CardTitle>
        <CardDescription>
          Answers use only your organization&apos;s latest risk report stored on this system — not the public web.
          Always verify before acting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasReport ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100/90">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>No risk report yet. Run a risk scan on this page, then ask questions about the report.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Summarize the top risks for executives, or what should we patch first?"
              disabled={loading}
              rows={compact ? 3 : 4}
              className={cn(
                'flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm placeholder:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={loading}>
                <Send className="mr-2 h-4 w-4" />
                {loading ? 'Thinking…' : 'Get assist'}
              </Button>
              {last?.review_required ? (
                <Badge variant="warning" className="text-xs">
                  Human review required
                </Badge>
              ) : null}
            </div>
          </form>
        )}

        {last?.answer ? (
          <div
            className={cn(
              'rounded-md border border-border bg-card/80 p-4 text-sm leading-relaxed text-card-foreground',
              compact && 'max-h-64 overflow-y-auto'
            )}
          >
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Response</p>
            <div className="whitespace-pre-wrap">{last.answer}</div>
            {last.sources?.length ? (
              <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Sources: </span>
                {last.sources.map((s, i) => (
                  <span key={i} className="font-mono">
                    {s.report_id ? `report ${s.report_id}` : 'risk report'}
                    {i < last.sources.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </div>
            ) : null}
            {last.mitigations_applied?.length ? (
              <details className="mt-3 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none text-foreground/80">Safeguards applied</summary>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  {last.mitigations_applied.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
